import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import ScrollToTop from "./components/ScrollToTop";
import LanguageRedirectHandler from "./components/LanguageRedirectHandler";
import CookieBanner from "./components/CookieBanner";

// Redirect wrapper for /guide/:slug to /en/guide/:slug
const GuideRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/en/guide/${slug}`} replace />;
};

// Redirect wrapper for /blog/:slug to /en/blog/:slug
const BlogRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/en/blog/${slug}`} replace />;
};

// Critical pages - loaded immediately
import LocalizedLandingPage from "./pages/LocalizedLandingPage";

// Lazy loaded pages - code splitting for better performance
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LocalizedBlogPage = lazy(() => import("./pages/LocalizedBlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ProgrammaticSEO = lazy(() => import("./pages/ProgrammaticSEO"));
const ProgrammaticPage = lazy(() => import("./pages/ProgrammaticPage"));
const PSEOPage = lazy(() => import("./pages/PSEOPage"));
const ContentPreview = lazy(() => import("./pages/ContentPreview"));
const CasinoManager = lazy(() => import("./pages/CasinoManager"));
const ContentScheduler = lazy(() => import("./pages/ContentScheduler"));
const PublicationScheduler = lazy(() => import("./pages/PublicationScheduler"));
const PillarGuidePage = lazy(() => import("./pages/PillarGuidePage"));
const GuidesIndexPage = lazy(() => import("./pages/GuidesIndexPage"));
const SEOAuditTool = lazy(() => import("./pages/SEOAuditTool"));
const SEOAuditSpokes = lazy(() => import("./pages/SEOAuditSpokes"));
const SEOAuditPSEO = lazy(() => import("./pages/SEOAuditPSEO"));
const SiteAuditPage = lazy(() => import("./pages/SiteAuditPage"));
const Google2026Audit = lazy(() => import("./pages/Google2026Audit"));
const DataForSEOAuditPage = lazy(() => import("./pages/DataForSEOAuditPage"));
const StatisticsPage = lazy(() => import("./pages/StatisticsPage"));
const AdminGate = lazy(() => import("./AdminGate"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ResponsibleGamblingPage = lazy(() => import("./pages/ResponsibleGamblingPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AffiliateDisclosurePage = lazy(() => import("./pages/AffiliateDisclosurePage"));
const Methodology = lazy(() => import("./pages/Methodology"));
const EditorialGuidelines = lazy(() => import("./pages/EditorialGuidelines"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const TopicClusterManager = lazy(() => import("./pages/TopicClusterManager"));
const WhaleTrackerPage = lazy(() => import("./pages/WhaleTrackerPage"));
const WhaleAdminPage = lazy(() => import("./pages/WhaleAdminPage"));
const CashbackCalculatorPage = lazy(() => import("./pages/CashbackCalculatorPage"));
const ContentSyncPage = lazy(() => import("./pages/ContentSyncPage"));

// Loading fallback component - minimal and fast
const PageLoader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="w-10 h-10 border-3 border-[#A855F7]/20 border-t-[#A855F7] rounded-full animate-spin" />
  </div>
);

// Admin wrapper with lazy AdminGate
const AdminRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    <AdminGate>{children}</AdminGate>
  </Suspense>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <div className="noise-overlay" />
        <BrowserRouter>
          <ScrollToTop />
          <CookieBanner />
          <LanguageRedirectHandler>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Root redirect to /en */}
                <Route path="/" element={<LocalizedLandingPage />} />
                
                {/* Localized Landing Pages - Not lazy loaded for fastest initial render */}
                <Route path="/en" element={<LocalizedLandingPage />} />
                <Route path="/de" element={<LocalizedLandingPage />} />
                <Route path="/nl" element={<LocalizedLandingPage />} />
                <Route path="/en-ca" element={<LocalizedLandingPage />} />
                <Route path="/da" element={<LocalizedLandingPage />} />
                
                {/* Pillar Guide Pages - Redirect /guide/:slug to /en/guide/:slug */}
                <Route path="/guide/:slug" element={<GuideRedirect />} />
                <Route path="/en/guide/:slug" element={<PillarGuidePage />} />
                <Route path="/de/guide/:slug" element={<PillarGuidePage />} />
                <Route path="/nl/guide/:slug" element={<PillarGuidePage />} />
                <Route path="/en-ca/guide/:slug" element={<PillarGuidePage />} />
                <Route path="/da/guide/:slug" element={<PillarGuidePage />} />
                
                {/* Guides Index Pages */}
                <Route path="/guides" element={<GuidesIndexPage />} />
                <Route path="/en/guides" element={<GuidesIndexPage />} />
                <Route path="/de/guides" element={<GuidesIndexPage />} />
                <Route path="/nl/guides" element={<GuidesIndexPage />} />
                <Route path="/en-ca/guides" element={<GuidesIndexPage />} />
                <Route path="/da/guides" element={<GuidesIndexPage />} />
                
                {/* Localized Blog Pages */}
                <Route path="/blog" element={<LocalizedBlogPage />} />
                <Route path="/en/blog" element={<LocalizedBlogPage />} />
                <Route path="/de/blog" element={<LocalizedBlogPage />} />
                <Route path="/nl/blog" element={<LocalizedBlogPage />} />
                <Route path="/en-ca/blog" element={<LocalizedBlogPage />} />
                <Route path="/da/blog" element={<LocalizedBlogPage />} />
                
                {/* Article Pages (localized) - Redirect /blog/:slug to /en/blog/:slug */}
                <Route path="/blog/:slug" element={<BlogRedirect />} />
                <Route path="/en/blog/:slug" element={<ArticlePage />} />
                <Route path="/de/blog/:slug" element={<ArticlePage />} />
                <Route path="/nl/blog/:slug" element={<ArticlePage />} />
                <Route path="/en-ca/blog/:slug" element={<ArticlePage />} />
                <Route path="/da/blog/:slug" element={<ArticlePage />} />
                
                {/* PSEO Pages (localized) */}
                <Route path="/en/pseo/:slug" element={<PSEOPage />} />
                <Route path="/de/pseo/:slug" element={<PSEOPage />} />
                <Route path="/nl/pseo/:slug" element={<PSEOPage />} />
                <Route path="/en-ca/pseo/:slug" element={<PSEOPage />} />
                <Route path="/da/pseo/:slug" element={<PSEOPage />} />
                
                {/* Statistics Pages (localized) */}
                <Route path="/statistics" element={<StatisticsPage />} />
                <Route path="/en/statistics" element={<StatisticsPage />} />
                <Route path="/de/statistics" element={<StatisticsPage />} />
                <Route path="/nl/statistics" element={<StatisticsPage />} />
                <Route path="/en-ca/statistics" element={<StatisticsPage />} />
                <Route path="/da/statistics" element={<StatisticsPage />} />
                
                {/* Legal & Compliance Pages - E-E-A-T (localized) */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/en/about" element={<AboutPage />} />
                <Route path="/de/about" element={<AboutPage />} />
                <Route path="/nl/about" element={<AboutPage />} />
                <Route path="/en-ca/about" element={<AboutPage />} />
                <Route path="/da/about" element={<AboutPage />} />
                
                {/* E-E-A-T Support Pages - Methodology (Google 2026) */}
                <Route path="/methodology" element={<Methodology />} />
                <Route path="/en/methodology" element={<Methodology />} />
                <Route path="/de/methodology" element={<Methodology />} />
                <Route path="/nl/methodology" element={<Methodology />} />
                <Route path="/en-ca/methodology" element={<Methodology />} />
                <Route path="/da/methodology" element={<Methodology />} />
                
                {/* E-E-A-T Support Pages - Editorial Guidelines (Google 2026) */}
                <Route path="/editorial-guidelines" element={<EditorialGuidelines />} />
                <Route path="/en/editorial-guidelines" element={<EditorialGuidelines />} />
                <Route path="/de/editorial-guidelines" element={<EditorialGuidelines />} />
                <Route path="/nl/editorial-guidelines" element={<EditorialGuidelines />} />
                <Route path="/en-ca/editorial-guidelines" element={<EditorialGuidelines />} />
                <Route path="/da/editorial-guidelines" element={<EditorialGuidelines />} />
                
                <Route path="/responsible-gambling" element={<ResponsibleGamblingPage />} />
                <Route path="/en/responsible-gambling" element={<ResponsibleGamblingPage />} />
                <Route path="/de/responsible-gambling" element={<ResponsibleGamblingPage />} />
                <Route path="/nl/responsible-gambling" element={<ResponsibleGamblingPage />} />
                <Route path="/en-ca/responsible-gambling" element={<ResponsibleGamblingPage />} />
                <Route path="/da/responsible-gambling" element={<ResponsibleGamblingPage />} />
                
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/en/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/de/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/nl/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/en-ca/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/da/privacy" element={<PrivacyPolicyPage />} />
                
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/en/terms" element={<TermsPage />} />
                <Route path="/de/terms" element={<TermsPage />} />
                <Route path="/nl/terms" element={<TermsPage />} />
                <Route path="/en-ca/terms" element={<TermsPage />} />
                <Route path="/da/terms" element={<TermsPage />} />
                
                <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                <Route path="/en/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                <Route path="/de/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                <Route path="/nl/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                <Route path="/en-ca/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                <Route path="/da/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                
                {/* Admin Routes - All lazy loaded */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/clusters" element={<AdminRoute><TopicClusterManager /></AdminRoute>} />
                <Route path="/admin/pseo" element={<AdminRoute><ProgrammaticSEO /></AdminRoute>} />
                <Route path="/admin/casinos" element={<AdminRoute><CasinoManager /></AdminRoute>} />
                <Route path="/admin/scheduler" element={<AdminRoute><ContentScheduler /></AdminRoute>} />
                <Route path="/admin/publication-scheduler" element={<AdminRoute><PublicationScheduler /></AdminRoute>} />
                <Route path="/admin/seo-audit" element={<AdminRoute><SEOAuditTool /></AdminRoute>} />
                <Route path="/admin/seo-audit-spokes" element={<AdminRoute><SEOAuditSpokes /></AdminRoute>} />
                <Route path="/admin/seo-audit-pseo" element={<AdminRoute><SEOAuditPSEO /></AdminRoute>} />
                <Route path="/admin/site-audit" element={<AdminRoute><SiteAuditPage /></AdminRoute>} />
                <Route path="/admin/google-2026" element={<AdminRoute><Google2026Audit /></AdminRoute>} />
                <Route path="/admin/dataforseo" element={<AdminRoute><DataForSEOAuditPage /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
                <Route path="/admin/whale-tools" element={<AdminRoute><WhaleAdminPage /></AdminRoute>} />
                <Route path="/admin/content-sync" element={<AdminRoute><ContentSyncPage /></AdminRoute>} />
                
                {/* Admin Preview Routes */}
                <Route path="/admin/preview/:type/:lang/:slug" element={<AdminRoute><ContentPreview /></AdminRoute>} />
                
                {/* User Registration & Auth */}
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/dashboard" element={<UserDashboard />} />
                
                {/* Whale Tracker - Public (Localized) */}
                <Route path="/en/whale-tracker" element={<WhaleTrackerPage />} />
                <Route path="/de/whale-tracker" element={<WhaleTrackerPage />} />
                <Route path="/nl/whale-tracker" element={<WhaleTrackerPage />} />
                <Route path="/da/whale-tracker" element={<WhaleTrackerPage />} />
                <Route path="/en-ca/whale-tracker" element={<WhaleTrackerPage />} />
                {/* Redirect old URL to default language */}
                <Route path="/whale-tracker" element={<Navigate to="/en/whale-tracker" replace />} />
                
                {/* Cashback Calculator - Public (Localized) */}
                <Route path="/en/cashback-calculator" element={<CashbackCalculatorPage />} />
                <Route path="/de/cashback-calculator" element={<CashbackCalculatorPage />} />
                <Route path="/nl/cashback-calculator" element={<CashbackCalculatorPage />} />
                <Route path="/da/cashback-calculator" element={<CashbackCalculatorPage />} />
                <Route path="/en-ca/cashback-calculator" element={<CashbackCalculatorPage />} />
                <Route path="/cashback-calculator" element={<Navigate to="/en/cashback-calculator" replace />} />
                
                {/* Other Content Routes */}
                <Route path="/g/:slug" element={<ProgrammaticPage />} />
              </Routes>
            </Suspense>
          </LanguageRedirectHandler>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#111111",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
